'use client'

import { type Signal, batch, signal } from '@preact/signals-react'
import {
  useComputed,
  useSignal,
  useSignals,
} from '@preact/signals-react/runtime'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { usePopularSearchQuery } from '@susu/headless-commerce/gql/generated/getPopularSearchPhrases.urql'
import { useSearchSuggestionsQuery } from '@susu/headless-commerce/gql/generated/searchSuggestions.urql'
import { useDebouncedSignal } from '@susu/headless-commerce/hooks/useDebouncedSignal'
import { useDeepCompareMemoizeSignal } from '@susu/headless-commerce/hooks/useDeepCompareMemoizeSignal'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { convertLocaleToCommerceGraphQLFormat } from '@susu/headless-commerce/utils/localeUtils'
import log from '@susu/headless-commerce/utils/log'
import { enableScroll } from '@susu/headless-commerce/utils/scrollUtils'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackEvent } from '@susu/headless-commerce/utils/tracking/tracking'
import { isNotUndefined } from '@susu/undefined'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

export type SearchState = 'idle' | 'loading' | 'failed' | 'succeeded'

export type SearchInputState = 'empty' | 'valid' | 'invalid'

export type ClientNavContext = {
  isNavOpened: Signal<boolean>
  openNav: () => void
  closeNav: () => void

  isSubMenuOpened: Signal<Record<string, boolean>>
  openSubMenu: (name: string) => void
  closeSubMenu: () => void

  isSearchMenuOpened: Signal<boolean>
  openSearchMenu: () => void

  isSearchOpenFromHeader: Signal<boolean>
  setIsSearchOpenFromHeader: (value: boolean) => void

  searchState: Signal<SearchState>
  searchInputState: Signal<SearchInputState>
  searchSuggestionsQuery: Signal<
    ReturnType<typeof useSearchSuggestionsQuery>['0']
  >
  popularSearchQuery: Signal<ReturnType<typeof usePopularSearchQuery>['0']>
  searchQuery: Signal<string>
  debouncedSearchQuery: Signal<string>
  setSearchQuery: (query: string) => void
  clearSearchQuery: () => void
  isSearchDebouncing: Signal<boolean>
  setIsSearchDebouncing: (value: boolean) => void
  debouncedTrackEvent: (searchTerm: string) => void

  isCountrySelectorOpened: Signal<boolean>
  isCountrySelectorOpenedFromFooter: Signal<boolean>
  openCountrySelector: (footer?: boolean) => void

  isLanguageSelectorOpened: Signal<boolean>
  isLanguageSelectorOpenedFromFooter: Signal<boolean>
  openLanguageSelector: (footer?: boolean) => void

  isCardsLoaded: Signal<boolean>

  criteria: Signal<string>
  hasCriteria: () => boolean
  setCriteria: (value: string) => void
  clearCriteria: () => void
}

export const ClientNavContextDefaults: ClientNavContext = {
  isNavOpened: signal(false),
  openNav: () => {},
  closeNav: () => {},

  isSubMenuOpened: signal({}),
  openSubMenu: () => {},
  closeSubMenu: () => {},

  isSearchMenuOpened: signal(false),
  openSearchMenu: () => {},

  isSearchOpenFromHeader: signal(false),
  setIsSearchOpenFromHeader: () => {},

  searchState: signal('idle'),
  searchInputState: signal('empty'),
  searchSuggestionsQuery: signal({
    fetching: false,
    stale: false,
  }),
  popularSearchQuery: signal({
    fetching: false,
    stale: false,
  }),
  searchQuery: signal(''),
  debouncedSearchQuery: signal(''),
  setSearchQuery: () => {},
  clearSearchQuery: () => {},
  isSearchDebouncing: signal(false),
  isCountrySelectorOpened: signal(false),
  isCountrySelectorOpenedFromFooter: signal(false),
  openCountrySelector: () => {},
  setIsSearchDebouncing: () => {},
  debouncedTrackEvent: () => {},
  isLanguageSelectorOpened: signal(false),
  isLanguageSelectorOpenedFromFooter: signal(false),
  openLanguageSelector: () => {},

  isCardsLoaded: signal(false),

  criteria: signal(''),
  hasCriteria: () => false,
  setCriteria: () => {},
  clearCriteria: () => {},
}

export const ClientNavContext = createContext<ClientNavContext>(
  ClientNavContextDefaults,
)

export const useClientNav = () => useContext(ClientNavContext)

export type ClientNavProviderProps = {
  children: ReactNode
}

export const ClientNavProvider = ({ children }: ClientNavProviderProps) => {
  useSignals()
  const locale = useLocale()
  const country = useCountry()
  const { url, locationId } = URLData()
  const pageType = usePageType()
  const {
    siteID,
    ecommerce: { currencyCode },
  } = country

  const isNavOpened = useSignal(ClientNavContextDefaults.isNavOpened.value)
  const isCardsLoaded = useSignal(ClientNavContextDefaults.isCardsLoaded.value)
  const isSearchDebouncingRef = useRef(false)

  const openNav = useCallback(() => {
    log.debug({
      method: 'ClientNavProvider:openNav',
    })
    isNavOpened.value = true
    isCardsLoaded.value = true
  }, [isNavOpened, isCardsLoaded])

  const closeNav = useCallback(() => {
    log.debug({
      method: 'ClientNavProvider:closeNav',
    })
    isNavOpened.value = false
    clearCriteria()
  }, [isNavOpened])

  const isSubMenuOpened = useSignal<
    ClientNavContext['isSubMenuOpened']['value']
  >(ClientNavContextDefaults.isSubMenuOpened.value)

  const openSubMenu = useCallback(
    (name: string) => {
      log.debug({
        method: 'ClientNavProvider:openSubMenu',
      })

      clearCriteria()
      clearSearchQuery()

      // Open only the one menu.
      isSubMenuOpened.value = {
        [name]: true,
      }
    },
    [isSubMenuOpened],
  )
  const closeSubMenu = useCallback(() => {
    log.debug({
      method: 'ClientSubMenuProvider:closeSubMenu',
    })

    batch(() => {
      isSubMenuOpened.value = {}
      if (
        isCountrySelectorOpenedFromFooter.value === true ||
        isLanguageSelectorOpenedFromFooter.value === true
      ) {
        setTimeout(() => {
          isCountrySelectorOpenedFromFooter.value = false
          isLanguageSelectorOpenedFromFooter.value = false
        }, 400)
      }
    })
    clearSearchQuery()
    clearCriteria()
  }, [isSubMenuOpened])

  const isSearchMenuOpened = useComputed<boolean>(() => {
    return isSubMenuOpened.value['SearchMenu'] === true
  })
  const openSearchMenu = useCallback(() => {
    log.debug({
      method: 'ClientSubMenuProvider:openSearchMenu',
    })
    openSubMenu('SearchMenu')
  }, [openSubMenu])

  const isCountrySelectorOpened = useComputed<boolean>(() => {
    return isSubMenuOpened.value['CountrySelector'] === true
  })
  const isCountrySelectorOpenedFromFooter = useSignal<boolean>(false)
  const openCountrySelector = useCallback(
    (footer = false) => {
      log.debug({
        method: 'ClientSubMenuProvider:openCountrySelector',
      })
      batch(() => {
        openSubMenu('CountrySelector')
        if (footer === true) {
          isCountrySelectorOpenedFromFooter.value = true
        }
      })
    },
    [openSubMenu, isCountrySelectorOpenedFromFooter],
  )

  const isLanguageSelectorOpened = useComputed<boolean>(() => {
    return isSubMenuOpened.value['LanguageSelector'] === true
  })
  const isLanguageSelectorOpenedFromFooter = useSignal<boolean>(false)
  const openLanguageSelector = useCallback(
    (footer = false) => {
      log.debug({
        method: 'ClientSubMenuProvider:openLanguageSelector',
      })
      batch(() => {
        openSubMenu('LanguageSelector')
        if (footer === true) {
          isLanguageSelectorOpenedFromFooter.value = true
        }
      })
    },
    [openSubMenu, isLanguageSelectorOpenedFromFooter],
  )

  const searchQuery = useSignal<string>(
    ClientNavContextDefaults.searchQuery.value,
  )
  const searchInputState = useComputed<SearchInputState>(() => {
    if (searchQuery.value === '') {
      return 'empty'
    }
    return 'valid'
  })
  const debouncedSearchQuery = useDebouncedSignal(searchQuery, 800)
  const setSearchQuery = useCallback(
    (query: string) => {
      log.debug({
        method: 'ClientNavProvider:setSearchQuery',
        query,
      })

      searchQuery.value = query
    },
    [searchQuery],
  )
  const clearSearchQuery = useCallback(() => {
    log.debug({
      method: 'ClientNavProvider:clearSearchQuery',
    })

    searchQuery.value = ''
  }, [searchQuery])

  const [searchSuggestionsQueryValue, executeSearchSuggestionsQuery] =
    useSearchSuggestionsQuery({
      variables: useMemo(
        () => ({
          searchText: debouncedSearchQuery.value,
          siteInfo: {
            siteId: siteID,
            locale: convertLocaleToCommerceGraphQLFormat(locale),
            currency: currencyCode,
          },
        }),
        [currencyCode, locale, siteID, debouncedSearchQuery.value],
      ),
      context: useMemo(
        () => ({
          clientName: 'commerce',
        }),
        [],
      ),
      pause: true,
    })
  const searchSuggestionsQuery = useDeepCompareMemoizeSignal(
    searchSuggestionsQueryValue,
  )

  const searchState = useComputed<SearchState>(() => {
    if (searchSuggestionsQuery.value.fetching === true) {
      return 'loading'
    }

    if (isNotUndefined(searchSuggestionsQuery.value.error)) {
      return 'failed'
    }

    if (isNotUndefined(searchSuggestionsQuery.value.data)) {
      return 'succeeded'
    }

    return 'idle'
  })

  const [popularSearchQueryValue, executePopularSearchQuery] =
    usePopularSearchQuery({
      variables: useMemo(
        () => ({
          siteInfo: {
            siteId: siteID,
            locale: convertLocaleToCommerceGraphQLFormat(locale),
            currency: currencyCode,
          },
        }),
        [currencyCode, locale, siteID],
      ),
      context: useMemo(
        () => ({
          clientName: 'commerce',
        }),
        [],
      ),
      pause: true,
    })
  const popularSearchQuery = useDeepCompareMemoizeSignal(
    popularSearchQueryValue,
  )

  // Country selector
  const criteria = useSignal<string>(ClientNavContextDefaults.criteria.value)

  const hasCriteria = useCallback(() => {
    return criteria.value !== ''
  }, [criteria])

  const setCriteria = useCallback(
    (value: string) => {
      criteria.value = value
    },
    [criteria],
  )

  const clearCriteria = useCallback(() => {
    criteria.value = ''
  }, [criteria])

  const isSearchOpenFromHeader = useSignal<boolean>(false)
  const setIsSearchOpenFromHeader = useCallback(
    (value: boolean) => {
      isSearchOpenFromHeader.value = value
    },
    [isSearchOpenFromHeader],
  )

  const isSearchDebouncing = useSignal<boolean>(false)
  const setIsSearchDebouncing = useCallback(
    (value: boolean) => {
      isSearchDebouncingRef.current = value
      isSearchDebouncing.value = value
    },
    [isSearchDebouncing],
  )

  const debouncedTrackEvent = useCallback(
    (searchTerm: string) => {
      if (isSearchDebouncingRef.current) {
        return
      }

      isSearchDebouncingRef.current = true
      isSearchDebouncing.value = true

      trackEvent({
        segment: {
          enrichProps: {
            country,
            locale,
          },
          event: 'termSearched',
          properties: {
            term: searchTerm,
            url,
            locationId,
            pageType,
            eventLabel: '',
            eventCategory: 'global_interactions',
            eventLocation: isSearchOpenFromHeader.value ? 'header' : 'menu',
          },
        },
      })

      setTimeout(() => {
        isSearchDebouncingRef.current = false
        isSearchDebouncing.value = false
      }, 500)
    },
    [
      country,
      locale,
      url,
      locationId,
      pageType,
      isSearchOpenFromHeader.value,
      isSearchDebouncing,
    ],
  )

  const providerValue: ClientNavContext = useMemo(
    () =>
      ({
        clearSearchQuery,
        closeNav,
        closeSubMenu,
        debouncedSearchQuery,
        isCardsLoaded,
        isCountrySelectorOpened,
        isCountrySelectorOpenedFromFooter,
        isLanguageSelectorOpened,
        isLanguageSelectorOpenedFromFooter,
        isNavOpened,
        isSearchMenuOpened,
        isSubMenuOpened,
        isSearchOpenFromHeader,
        setIsSearchOpenFromHeader,
        openCountrySelector,
        openLanguageSelector,
        openNav,
        openSearchMenu,
        openSubMenu,
        popularSearchQuery,
        searchInputState,
        searchQuery,
        searchState,
        searchSuggestionsQuery: searchSuggestionsQuery,
        setSearchQuery,
        criteria,
        hasCriteria,
        setCriteria,
        clearCriteria,
        isSearchDebouncing,
        setIsSearchDebouncing,
        debouncedTrackEvent,
      }) satisfies ClientNavContext,
    [
      clearSearchQuery,
      closeNav,
      closeSubMenu,
      debouncedSearchQuery,
      isCardsLoaded,
      isCountrySelectorOpened,
      isCountrySelectorOpenedFromFooter,
      isLanguageSelectorOpened,
      isLanguageSelectorOpenedFromFooter,
      isNavOpened,
      isSearchMenuOpened,
      isSubMenuOpened,
      isSearchOpenFromHeader,
      setIsSearchOpenFromHeader,
      openCountrySelector,
      openLanguageSelector,
      openNav,
      openSearchMenu,
      openSubMenu,
      popularSearchQuery,
      searchInputState,
      searchQuery,
      searchState,
      searchSuggestionsQuery,
      setSearchQuery,
      criteria,
      hasCriteria,
      setCriteria,
      clearCriteria,
      isSearchDebouncing,
      setIsSearchDebouncing,
      debouncedTrackEvent,
    ],
  )

  // Search suggestions query.
  useEffect(() => {
    if (searchInputState.value !== 'valid') {
      return
    }

    if (debouncedSearchQuery.value === '') {
      return
    }

    if (debouncedSearchQuery.value.length < 3) {
      return
    }

    executeSearchSuggestionsQuery()
  }, [
    searchInputState.value,
    debouncedSearchQuery.value,
    executeSearchSuggestionsQuery,
  ])

  // Popular search query.
  useEffect(() => {
    executePopularSearchQuery()
  }, [executePopularSearchQuery])

  // Close nav on escape.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isNavOpened.value === true) {
        closeSubMenu()
        closeNav()
        enableScroll()
      }
    },
    [isNavOpened.value, closeNav, closeSubMenu],
  )
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  log.debug({
    method: 'ClientNavProvider',
    providerValue,
  })

  return (
    <ClientNavContext.Provider value={providerValue}>
      {children}
    </ClientNavContext.Provider>
  )
}
